/**
 * Convert rendered macro HTML to WhatsApp-friendly plain text.
 *
 * WhatsApp ignores HTML on paste and uses its own markdown:
 *   *bold*   _italic_   ~strike~   `mono`
 *   bare URLs auto-link · real \n preserved · no underline
 *
 * Markers cannot have whitespace immediately inside them
 * (`* foo *` will not bold) — the wrapper logic trims/shifts whitespace out.
 *
 * Input: an HTML string from a rendered macro preview (Puck or RichTextEditor).
 * Output: a single plain-text string suitable for the system clipboard.
 */

const BLOCK_TAGS = new Set([
    "address",
    "article",
    "aside",
    "blockquote",
    "div",
    "dl",
    "dt",
    "dd",
    "fieldset",
    "figcaption",
    "figure",
    "footer",
    "form",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "header",
    "main",
    "nav",
    "ol",
    "p",
    "pre",
    "section",
    "table",
    "tbody",
    "thead",
    "tfoot",
    "tr",
    "ul",
]);

const HEADING_TAGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

interface Context {
    /** Stack of list types (ul=bullet, ol=numeric); used so nested lists know what to emit. */
    listStack: Array<"ul" | "ol">;
    /** Counter per current `<ol>`, indexed by listStack depth. */
    orderedIndex: number[];
}

/**
 * Wrap inline content in a marker, shifting leading/trailing whitespace outside.
 * WhatsApp treats `*foo*` as bold but `* foo *` as literal asterisks.
 */
function wrapInline(inner: string, marker: string): string {
    if (!inner) return "";
    const leading = inner.match(/^\s+/)?.[0] ?? "";
    const trailing = inner.match(/\s+$/)?.[0] ?? "";
    const core = inner.slice(leading.length, inner.length - trailing.length);
    if (!core) return inner; // marker around pure whitespace would be literal asterisks
    return `${leading}${marker}${core}${marker}${trailing}`;
}

function renderNode(node: Node, ctx: Context): string {
    if (node.nodeType === Node.TEXT_NODE) {
        // Collapse runs of whitespace within a text node, like a browser would.
        // Preserve the fact that whitespace exists (don't strip entirely).
        return (node.textContent ?? "").replace(/\s+/g, " ");
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return "";
    }

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    switch (tag) {
        case "br":
            return "\n";

        case "hr":
            return "\n———\n";

        case "script":
        case "style":
        case "head":
        case "noscript":
            return "";

        case "img": {
            const alt = el.getAttribute("alt");
            return alt ? alt : "";
        }

        case "a": {
            const href = el.getAttribute("href") ?? "";
            const text = renderChildren(el, ctx).trim();
            if (!href || href === "#") return text;
            // If the visible text already equals the href, just emit the URL once
            // (WhatsApp will auto-link). Otherwise emit `text: url`.
            if (text === href || !text) return href;
            return `${text}: ${href}`;
        }

        case "strong":
        case "b":
            return wrapInline(renderChildren(el, ctx), "*");

        case "em":
        case "i":
            return wrapInline(renderChildren(el, ctx), "_");

        case "s":
        case "strike":
        case "del":
            return wrapInline(renderChildren(el, ctx), "~");

        case "code":
        case "tt":
            return wrapInline(renderChildren(el, ctx), "`");

        case "pre": {
            // Use a code block fence; WhatsApp renders ```...``` monospace.
            const inner = renderChildren(el, ctx).trim();
            return inner ? `\n\`\`\`\n${inner}\n\`\`\`\n` : "";
        }

        case "u":
            // WhatsApp has no underline; emit children unchanged.
            return renderChildren(el, ctx);

        case "ul":
        case "ol": {
            ctx.listStack.push(tag);
            ctx.orderedIndex.push(0);
            const inner = renderChildren(el, ctx);
            ctx.listStack.pop();
            ctx.orderedIndex.pop();
            // Surround with newlines so the list reads as a block.
            return `\n${inner.replace(/\n+$/, "")}\n`;
        }

        case "li": {
            const depth = Math.max(0, ctx.listStack.length - 1);
            const indent = "  ".repeat(depth);
            const currentList = ctx.listStack[ctx.listStack.length - 1] ?? "ul";
            let marker: string;
            if (currentList === "ol") {
                ctx.orderedIndex[ctx.orderedIndex.length - 1] += 1;
                marker = `${ctx.orderedIndex[ctx.orderedIndex.length - 1]}. `;
            } else {
                marker = "• ";
            }
            const inner = renderChildren(el, ctx).trim().replace(/\n/g, `\n${indent}  `);
            return `${indent}${marker}${inner}\n`;
        }

        // Heading: emit on its own block, bolded (WhatsApp has no semantic h1-h6).
        case "h1":
        case "h2":
        case "h3":
        case "h4":
        case "h5":
        case "h6": {
            const inner = renderChildren(el, ctx).trim();
            return inner ? `\n${wrapInline(inner, "*")}\n` : "";
        }

        default: {
            const inner = renderChildren(el, ctx);
            // Block elements need surrounding newlines so paragraphs etc. separate cleanly.
            if (BLOCK_TAGS.has(tag) || HEADING_TAGS.has(tag)) {
                return `\n${inner}\n`;
            }
            return inner;
        }
    }
}

function renderChildren(el: Element, ctx: Context): string {
    let out = "";
    for (const child of Array.from(el.childNodes)) {
        out += renderNode(child, ctx);
    }
    return out;
}

/**
 * Normalize whitespace in the assembled output:
 *  - trim trailing spaces from every line
 *  - collapse 3+ blank lines down to 2 (preserves intentional paragraph gaps)
 *  - trim leading/trailing empty lines from the document
 *  - collapse runs of inline whitespace (multiple spaces → single space)
 */
function normalize(text: string): string {
    let out = text.replace(/[ \t]+\n/g, "\n");
    // Collapse runs of mid-line whitespace, but preserve leading indentation
    // so nested-list bullets keep their indent.
    out = out.replace(/(\S) {2,}/g, "$1 ");
    out = out.replace(/\n{3,}/g, "\n\n");
    return out.replace(/^\s+|\s+$/g, "");
}

export function htmlToWhatsApp(html: string): string {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstChild as Element | null;
    if (!root) return "";
    const ctx: Context = { listStack: [], orderedIndex: [] };
    return normalize(renderChildren(root, ctx));
}
